import { matchPath } from "react-router";
import { Building2, Handshake, Home, LineChart, Settings, Umbrella, Users , TrendingUp, ShieldCheck, Wallet, CalendarClock, Newspaper, CopyX, MessagesSquare, Store } from "lucide-react";
import type { Modulo } from "../organizacoes/useOrganizacaoAtiva";

export type SidebarItem = {
  label: string;
  to: string;
  icon: React.ReactNode;
  badge?: string;
  adminOnly?: boolean;
  /** Item de super admin que a conta demo também vê (migration 286). */
  liberadoNaDemo?: boolean;
  /** Só a conta demo vê. */
  soDemo?: boolean;
  /**
   * Item do Fluxo de Caixa: quem vê não é super admin, é quem está na lista da
   * aba (migration 196). Os quatro que a usam JÁ são super admin, então
   * `adminOnly` mostraria pra gente que não pode entrar.
   */
  exigeFluxoCaixa?: boolean;
  /** Previsão Servopa: lista própria da aba (migration 212), mesma lógica do caixa. */
  exigePrevisaoServopa?: boolean;
  external?: boolean;
  /** Módulo da organização (migration 279): sem ele, o item não aparece. */
  modulo?: Modulo;
  /** Só gerente ou super admin (chaves de API, migration 276). */
  exigeGerente?: boolean;
  /** Só super admin ou administrador da organização (unidades, migration 308). */
  exigeAdministrador?: boolean;
  /** Indicações (migration 287): só gerente de escritório que é parceira com link. */
  exigeParceira?: boolean;
  /** Seguros (298): super admin ou quem tem corretora vinculada (a RLS da 206 recorta). */
  exigeCorretora?: boolean;
  /** Ponto de "tem coisa nova aqui". */
  dot?: boolean;
  /** Quantos itens novos: número vermelho no canto do ícone (Feed, Mensagens). */
  contador?: number;
};

/**
 * Seções que usam o Layout (com barra lateral) mas NÃO são produto.
 *
 * Demais Produtos e Acesso Restrito viviam em tela cheia, sem menu nenhum, com um
 * link "voltar" solto no topo — quem entrava ficava sem as rotas comuns
 * (Contatos) e sem o caminho de volta que o resto do app tem. Agora entram no
 * Layout, mas com um menu REDUZIDO: o menu do consórcio aqui seria mentira
 * (Vendas, Comissões e Grupos não são desta seção).
 *
 * Fica em módulo próprio, e não dentro do Sidebar, porque `secaoDaRota` é
 * lógica pura e testável — importá-la do Sidebar arrastava o `ra-core` junto
 * (que toca localStorage no import e não sobe em teste).
 */
export const SECOES_SEM_PRODUTO: {
  /** `/users` entra na seção de Acesso Restrito mesmo não sendo `/admin/*`:
   *  é de lá que se chega nela, e sem isto clicar em "Usuários" trocava o
   *  menu inteiro pro do consórcio no meio da administração. */
  prefixos: string[];
  titulo: string;
  itens: SidebarItem[];
}[] = [
  {
    prefixos: ["/demais-ramos"],
    titulo: "North Demais Produtos",
    itens: [
      { label: "Cotações", to: "/demais-ramos", icon: <Umbrella className="w-5 h-5" /> },
      // Contatos DA SEÇÃO, não `/contacts`: aquela é rota compartilhada e
      // devolve o menu do produto lembrado (Consórcio, quase sempre),
      // chutando a pessoa pra fora de Demais Produtos sem ela ter pedido.
      { label: "Contatos", to: "/demais-ramos/contatos", icon: <Users className="w-5 h-5" /> },
      // Feed DA SEÇÃO pelo mesmo motivo de Contatos: `/feed` é compartilhada e
      // abriria com o menu do produto lembrado.
      { label: "Feed", to: "/demais-ramos/feed", icon: <Newspaper className="w-5 h-5" />, modulo: "feed" },
      { label: "Mensagens", to: "/demais-ramos/mensagens", icon: <MessagesSquare className="w-5 h-5" />, modulo: "mensagens" },
    ],
  },
  {
    prefixos: ["/admin", "/users"],
    titulo: "Acesso Restrito",
    itens: [
      { label: "Início", to: "/admin", icon: <Home className="w-5 h-5" /> },
      { label: "Usuários", to: "/users", icon: <Users className="w-5 h-5" /> },
      // Escritórios clientes (migration 279): só o super admin da North.
      { label: "Organizações", to: "/admin/organizacoes", icon: <Building2 className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
      // Escritórios/filiais (migration 308): super admin e administrador da organização.
      { label: "Unidades", to: "/admin/unidades", icon: <Store className="w-5 h-5" />, exigeAdministrador: true },
      // Pedidos dos links de contratação das parceiras (migration 287).
      { label: "Contratações", to: "/admin/contratacoes", icon: <Handshake className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
      { label: "Performance", to: "/admin/performance", icon: <TrendingUp className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
      {
        label: "Fluxo de Caixa",
        to: "/admin/fluxo-caixa",
        icon: <Wallet className="w-5 h-5" />,
        exigeFluxoCaixa: true,
        modulo: "fluxo_caixa",
      },
      {
        label: "Previsão Servopa",
        to: "/admin/previsao-servopa",
        icon: <CalendarClock className="w-5 h-5" />,
        exigePrevisaoServopa: true,
      },
      // Celular repetido entre contatos (migration 241): todo super admin, e só ele.
      { label: "Duplicidades", to: "/admin/duplicidades", icon: <CopyX className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
      { label: "Configurações", to: "/admin/configuracoes", icon: <Settings className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
      { label: "Estatísticas de uso", to: "/admin/insights", icon: <LineChart className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
      { label: "Auditoria", to: "/admin/auditoria", icon: <ShieldCheck className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
    ],
  },
];

/**
 * A seção sem produto da rota atual, ou null quando é rota de produto.
 *
 * Casa por SEGMENTO, não por prefixo de string: `/administradoras` não é
 * `/admin`.
 */
export const secaoDaRota = (pathname: string) =>
  SECOES_SEM_PRODUTO.find((s) =>
    s.prefixos.some((p) => pathname === p || pathname.startsWith(`${p}/`)),
  ) ?? null;

/**
 * Qual item do menu fica aceso — UM só, o de casamento mais específico.
 *
 * Casar "o item cuja rota é prefixo da atual" acende o PAI junto com o filho:
 * em `/demais-ramos/contatos`, "Cotações" (`/demais-ramos`) também casa e
 * ficavam os dois acesos. Valia igual pro "Início" de Seguros (`/seguros`)
 * sobre `/seguros/apolices` e pro do Acesso Restrito (`/admin`) sobre
 * `/admin/configuracoes`.
 *
 * Devolve o `to` vencedor, ou null quando nenhum casa. Item externo (link pra
 * fora) nunca acende.
 */
export const rotaAcesa = (
  pathname: string,
  itens: { to: string; external?: boolean }[],
): string | null => {
  let melhor: string | null = null;
  for (const item of itens) {
    if (item.external) continue;
    const casa =
      matchPath(item.to, pathname) != null || matchPath(`${item.to}/*`, pathname) != null;
    if (!casa) continue;
    if (melhor === null || item.to.length > melhor.length) melhor = item.to;
  }
  return melhor;
};

/**
 * Seções do menu (redesign Premium North, 19/10/2026): os MESMOS itens de antes,
 * só agrupados com um título pequeno em cima. A regra é por rótulo dentro de cada
 * menu; item sem seção conhecida cai no grupo sem título, na ordem em que veio —
 * então um item novo nunca some do menu por falta de mapeamento.
 */
const SECOES_DO_MENU: Record<string, [string, string[]][]> = {
  consortium: [
    ["", ["Feed", "Mensagens", "Dashboard"]],
    ["Relacionamento", ["Contatos", "Clientes", "CRM"]],
    ["Vendas", ["Vendas", "Propostas", "Simulador", "Campanhas"]],
    ["Pós-venda", ["Contemplação", "Inadimplentes", "Grupos", "Cancelamentos"]],
    ["Gestão", ["Comissões", "Fluxo de Caixa", "Chaves de API", "Indicações"]],
    ["Inteligência", ["North IA", "Meu Agente"]],
  ],
  insurance: [
    ["", ["Feed", "Mensagens", "Dashboard"]],
    ["Relacionamento", ["Contatos", "Clientes", "CRM"]],
    ["Vendas", ["Cotação", "Propostas", "Apólices"]],
    ["Pós-venda", ["Cobrança", "Inadimplência", "Persistência", "Exigências"]],
    ["Gestão", ["Comissões", "Recebimentos", "Corretoras", "Azos"]],
    ["Inteligência", ["North IA", "Meu Agente"]],
  ],
  canopus: [
    ["", ["Início"]],
    ["Vendas", ["Planos", "Vendas", "Clientes", "Contatos"]],
    ["Gestão", ["Comissões", "Carteira"]],
  ],
  "Acesso Restrito": [
    ["", ["Início", "Usuários", "Organizações", "Contratações"]],
    ["Financeiro", ["Fluxo de Caixa", "Previsão Servopa", "Performance"]],
    ["Controle", ["Duplicidades", "Estatísticas de uso", "Auditoria"]],
    ["Sistema", ["Configurações"]],
  ],
};

export const agruparEmSecoes = <T extends { label: string }>(
  menu: string,
  itens: T[],
): { titulo: string; itens: T[] }[] => {
  const mapa = SECOES_DO_MENU[menu];
  if (!mapa) return [{ titulo: "", itens }];
  const usados = new Set<T>();
  const grupos = mapa.map(([titulo, rotulos]) => {
    const doGrupo = rotulos.flatMap((r) => itens.filter((i) => i.label === r && !usados.has(i)));
    doGrupo.forEach((i) => usados.add(i));
    return { titulo, itens: doGrupo };
  });
  const sobra = itens.filter((i) => !usados.has(i));
  if (sobra.length) grupos[0] = { ...grupos[0], itens: [...grupos[0].itens, ...sobra] };
  return grupos.filter((g) => g.itens.length > 0);
};
